import { useForm, Link } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { useOrgPath } from '@/lib/pathFor'

interface CarrierDetail {
  id: string
  name: string
  code: string | null
  contact_email: string | null
  contact_phone: string | null
  website: string | null
  notes: string | null
  active: boolean
}

interface Props {
  carrier: CarrierDetail
}

export default function CarriersEdit({ carrier }: Props) {
  const pathFor = useOrgPath()
  const { data, setData, patch, processing } = useForm({
    name: carrier.name,
    code: carrier.code || '',
    contact_email: carrier.contact_email || '',
    contact_phone: carrier.contact_phone || '',
    website: carrier.website || '',
    notes: carrier.notes || '',
    active: carrier.active,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(pathFor(`/settings/carriers/${carrier.id}`))
  }

  return (
    <>
      <header className="flex h-14 items-center gap-3 border-b px-8">
        <Link href={pathFor(`/settings/carriers/${carrier.id}`)} className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <h1 className="text-sm font-semibold">Edit Carrier</h1>
      </header>

      <div className="max-w-md p-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-xs">Name *</Label>
            <Input
              id="name"
              required
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              className="mt-1 h-9 text-[13px]"
            />
          </div>

          <div>
            <Label htmlFor="code" className="text-xs">SCAC Code *</Label>
            <Input
              id="code"
              required
              value={data.code}
              onChange={(e) => setData('code', e.target.value)}
              className="mt-1 h-9 text-[13px] font-mono"
              placeholder="e.g. HLCU"
            />
          </div>

          <div>
            <Label htmlFor="contact_email" className="text-xs">Contact Email</Label>
            <Input
              id="contact_email"
              type="email"
              value={data.contact_email}
              onChange={(e) => setData('contact_email', e.target.value)}
              className="mt-1 h-9 text-[13px]"
            />
          </div>

          <div>
            <Label htmlFor="contact_phone" className="text-xs">Contact Phone</Label>
            <Input
              id="contact_phone"
              value={data.contact_phone}
              onChange={(e) => setData('contact_phone', e.target.value)}
              className="mt-1 h-9 text-[13px]"
            />
          </div>

          <div>
            <Label htmlFor="website" className="text-xs">Website</Label>
            <Input
              id="website"
              value={data.website}
              onChange={(e) => setData('website', e.target.value)}
              className="mt-1 h-9 text-[13px]"
              placeholder="https://"
            />
          </div>

          <div>
            <Label htmlFor="notes" className="text-xs">Notes</Label>
            <Textarea
              id="notes"
              value={data.notes}
              onChange={(e) => setData('notes', e.target.value)}
              className="mt-1 text-[13px]"
              rows={3}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="active"
              checked={data.active}
              onCheckedChange={(checked) => setData('active', checked === true)}
            />
            <Label htmlFor="active" className="text-xs cursor-pointer">Active</Label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" size="sm" disabled={processing}>
              Save Changes
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

CarriersEdit.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
